import React from 'react';
import { FileTextIcon } from 'lucide-react';

interface PDFPreviewProps {
  pdfUrl?: string;
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
}

export const PDFPreview: React.FC<PDFPreviewProps> = ({
  pdfUrl,
  isLoading = false,
  error,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generazione anteprima PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center p-8">
          <FileTextIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Errore durante il caricamento del PDF
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Riprova
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center p-8">
          <FileTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            Nessun PDF da visualizzare
          </h3>
          <p className="text-gray-500">
            Genera il PDF per visualizzare l'anteprima
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-100 flex items-center justify-center">
      <div className="text-center p-8">
        <FileTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          PDF Preview
        </h3>
        <p className="text-gray-500 mb-4">
          Il file PDF è pronto per il download
        </p>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Apri PDF in nuova scheda
        </a>
      </div>
    </div>
  );
};

// Component for HTML preview (before PDF generation)
interface HTMLPreviewProps {
  html: string;
  className?: string;
}

export const HTMLPreview: React.FC<HTMLPreviewProps> = ({
  html,
  className = '',
}) => {
  return (
    <div className={`bg-white shadow-lg p-8 overflow-auto ${className}`}>
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};
